import api from './Api';
import { IArquivoRetorno } from '../interfaces/IArquivoRetorno';

/**
 * Filtro da tela "Consultar Lançamentos" (DEVN3-534). Todos os campos são opcionais. O mesmo
 * objeto alimenta a grade paginada e o disparo do export XLSX filtrado.
 */
export interface IConsultaLancamentosFiltro {
    contaContabil?: string;
    descricaoConta?: string;
    dataInicio?: string; // yyyy-MM-dd
    dataFim?: string;    // yyyy-MM-dd
    historico?: string;
    valorMin?: string;
    valorMax?: string;
    tipo?: string;                     // 'C' | 'D'
    status?: string[];
    chaveVinculo?: string;
    chaveVinculoEstado?: string;       // 'vazio' | 'preenchido'
    chaveVinculoMatch?: string;
    chaveVinculoMatchEstado?: string;
}

export interface IConsultaLancamentoItem {
    id: number;
    contaContabil: string | null;
    descricaoConta: string | null;
    dataLancamento: string;
    historico: string | null;
    valor: number | null;
    tipo: string | null;
    status: string | null;
    chaveVinculo: string | null;
    chaveVinculoMatch: string | null;
}

export interface IConsultaLancamentosPage {
    items: IConsultaLancamentoItem[];
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
}

export type ConsultaSortField =
    | 'conta' | 'descricao' | 'data' | 'historico' | 'valor'
    | 'tipo' | 'status' | 'chaveVinculo' | 'chaveVinculoMatch';

function montarQuery(filtro: IConsultaLancamentosFiltro): URLSearchParams {
    const q = new URLSearchParams();
    const set = (k: string, v?: string) => { if (v != null && v !== '') q.append(k, v); };

    set('contaContabil', filtro.contaContabil);
    set('descricaoConta', filtro.descricaoConta);
    set('dataInicio', filtro.dataInicio);
    set('dataFim', filtro.dataFim);
    set('historico', filtro.historico);
    set('valorMin', filtro.valorMin);
    set('valorMax', filtro.valorMax);
    set('tipo', filtro.tipo);
    set('chaveVinculo', filtro.chaveVinculo);
    set('chaveVinculoEstado', filtro.chaveVinculoEstado);
    set('chaveVinculoMatch', filtro.chaveVinculoMatch);
    set('chaveVinculoMatchEstado', filtro.chaveVinculoMatchEstado);
    // List<string> Status: um par status=<valor> por item (bindável pelo ASP.NET Core).
    (filtro.status ?? []).forEach(s => q.append('status', s));

    return q;
}

class ConsultaLancamentosService {
    async consultar(
        esteiraId: number,
        filtro: IConsultaLancamentosFiltro,
        sortField: ConsultaSortField,
        sortDir: 'asc' | 'desc',
        page: number,
        perPage: number,
    ): Promise<IConsultaLancamentosPage> {
        const q = montarQuery(filtro);
        q.append('sortField', sortField);
        q.append('sortDir', sortDir);
        q.append('page', String(page));
        q.append('perPage', String(perPage));

        const { data } = await api.get<IConsultaLancamentosPage>(`Lancamento/consulta/${esteiraId}?${q.toString()}`);
        return data;
    }

    async statusDistintos(esteiraId: number): Promise<string[]> {
        const { data } = await api.get<string[]>(`Lancamento/consulta-status/${esteiraId}`);
        return data;
    }

    /** Envia o filtro no corpo — mesmo shape aceito pela grade. */
    async gerarXlsx(esteiraId: number, filtro: IConsultaLancamentosFiltro): Promise<{ operacaoId: number; arquivoRetornoId: number }> {
        const { data } = await api.post(`ArquivoRetorno/gerar-xlsx-consulta-lancamentos/${esteiraId}`, {
            contaContabil: filtro.contaContabil || null,
            descricaoConta: filtro.descricaoConta || null,
            dataInicio: filtro.dataInicio || null,
            dataFim: filtro.dataFim || null,
            historico: filtro.historico || null,
            valorMin: filtro.valorMin ? Number(filtro.valorMin) : null,
            valorMax: filtro.valorMax ? Number(filtro.valorMax) : null,
            tipo: filtro.tipo || null,
            status: filtro.status && filtro.status.length > 0 ? filtro.status : null,
            chaveVinculo: filtro.chaveVinculo || null,
            chaveVinculoEstado: filtro.chaveVinculoEstado || null,
            chaveVinculoMatch: filtro.chaveVinculoMatch || null,
            chaveVinculoMatchEstado: filtro.chaveVinculoMatchEstado || null,
        });
        return data;
    }

    async listarXlsx(esteiraId: number): Promise<IArquivoRetorno[]> {
        const { data } = await api.get<IArquivoRetorno[]>(`ArquivoRetorno/xlsx-consulta-lancamentos/${esteiraId}`);
        return data;
    }
}

export default new ConsultaLancamentosService();
